import { motion } from "framer-motion"; // eslint-disable-line no-unused-vars
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Zap, MapPin } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

function fadeIn(delay = 0) {
  return {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay },
    viewport: { once: true, amount: 0.2 },
  };
}

function ListItem({ children }) {
  return (
    <li className="flex items-start gap-2.5 text-gray-400">
      <Check className="w-4 h-4 text-brand-400 flex-shrink-0 mt-1" />
      <span>{children}</span>
    </li>
  );
}

export default function About(){
  return (
    <>
      <Helmet>
        <title>About SACVPN - Business VPN Provider Powered by WireGuard</title>
        <meta name="description" content="SACVPN provides VPN solutions for individuals, gamers, and businesses. A U.S.-based company offering WireGuard-based secure remote access, a no-logs policy for your browsing activity, and dedicated support for healthcare, legal, and financial teams." />
        <meta name="keywords" content="about SACVPN, business VPN provider, WireGuard VPN company, corporate VPN solution, U.S. VPN provider, managed VPN service" />
        <link rel="canonical" href="https://www.sacvpn.com/about" />
        <meta property="og:title" content="About SACVPN - Business VPN Provider Powered by WireGuard" />
        <meta property="og:description" content="SACVPN provides VPN solutions for individuals, gamers, and businesses. A U.S.-based company offering WireGuard-based secure remote access, a no-logs policy for your browsing activity, and dedicated support for healthcare, legal, and financial teams." />
      </Helmet>

      {/* Hero */}
      <section className="relative py-24 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-dark-900" />
        <div className="absolute inset-0 bg-[radial-gradient(900px_460px_at_50%_-5%,rgba(14,165,233,0.12),transparent_65%)]" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-400 mb-4">About SACVPN</p>
            <h1 className="text-4xl md:text-5xl font-display font-bold text-white leading-[1.05] tracking-tight">
              Why we built a VPN we would actually use
            </h1>
            <p className="mt-6 text-lg text-gray-400 leading-relaxed">
              SACVPN exists to make private, secure internet access available to everyone: individuals, gamers, and businesses.
              We are builders and network engineers who were tired of bloated VPNs, confusing pricing, and weak performance. So we
              designed a service powered by WireGuard that is simple, honest, and engineered for privacy protection.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">

        {/* Origin story */}
        <motion.div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8 md:p-10" {...fadeIn(0.05)}>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">Our story</h2>
          <p className="mt-4 text-gray-400 leading-relaxed">
            SACVPN started as a practical response to three problems we kept seeing: everyday people have their data collected,
            profiled, and sold without meaningful consent; many VPNs make bold promises but deliver inconsistent speeds,
            limits on devices, and dark patterns during checkout; and businesses want simple, predictable security for remote
            work without standing up a full enterprise stack. We believed there was room for a VPN that respected users and
            treated performance as a first-class requirement.
          </p>
          <p className="mt-4 text-gray-400 leading-relaxed">
            We chose WireGuard from day one. It is modern, minimal, and efficient. By building around WireGuard and avoiding
            legacy protocols unless absolutely required, we keep configuration simple and the footprint light. The result is a
            service where setup is measured in seconds, not afternoons.
          </p>
        </motion.div>

        {/* What we are building */}
        <motion.div className="grid md:grid-cols-2 gap-6 mt-12" {...fadeIn(0.1)}>
          <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
            <h3 className="text-xl font-semibold text-white mb-4">What we are building</h3>
            <ul className="space-y-2.5">
              <ListItem>A combined Personal / Gaming plan with unlimited devices so a whole household can be protected.</ListItem>
              <ListItem>Low-overhead WireGuard and unlimited devices; connect to the nearest US node and judge by your in-game ping.</ListItem>
              <ListItem>Business tiers with clear device quotas and a simple management dashboard.</ListItem>
              <ListItem>One-click device setup: generate a config, scan a QR code, and you are online.</ListItem>
              <ListItem>A no-logs policy enforced by design and process. We do not record browsing activity.</ListItem>
              <ListItem>Infrastructure that prioritizes a light footprint and regular updates.</ListItem>
            </ul>
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
            <h3 className="text-xl font-semibold text-white mb-4">The problem we are solving</h3>
            <ul className="space-y-2.5">
              <ListItem>ISPs throttle or shape certain traffic. A VPN prevents easy classification and slows throttling.</ListItem>
              <ListItem>Public and shared networks expose devices to snooping and credential theft.</ListItem>
              <ListItem>Smart TVs, consoles, and IoT devices leak metadata that can be tied to a household.</ListItem>
              <ListItem>Remote work often mixes personal and business devices without a clean separation.</ListItem>
              <ListItem>Many VPNs add limits, upsells, and friction that discourage consistent protection.</ListItem>
            </ul>
          </div>
        </motion.div>

        {/* Why people need a VPN */}
        <motion.div className="mt-12" {...fadeIn(0.15)}>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">Why people need a VPN at home and in public</h2>
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
              <h4 className="text-lg font-semibold text-white mb-3">At home</h4>
              <ul className="space-y-2.5">
                <ListItem>Prevent your ISP from collecting and selling your browsing patterns.</ListItem>
                <ListItem>Reduce throttling on streaming and cloud gaming during busy hours.</ListItem>
                <ListItem>Protect every device: phones, tablets, PCs, TVs, and IoT.</ListItem>
                <ListItem>Unlock additional streaming libraries when you travel.</ListItem>
                <ListItem>Mask your household IP to reduce targeted advertising.</ListItem>
                <ListItem>Keep financial activity private while shopping or banking.</ListItem>
              </ul>
            </div>
            <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
              <h4 className="text-lg font-semibold text-white mb-3">On public networks</h4>
              <ul className="space-y-2.5">
                <ListItem>Encrypt traffic on cafe, hotel, airplane, and campus Wi-Fi.</ListItem>
                <ListItem>Block trivial eavesdropping and credential interception.</ListItem>
                <ListItem>Prevent captive portals and intermediaries from profiling your activity.</ListItem>
                <ListItem>Maintain a consistent identity for apps that dislike IP changes.</ListItem>
                <ListItem>Connect quickly with lightweight WireGuard even on slower links.</ListItem>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* What makes SACVPN different */}
        <motion.div className="mt-12 rounded-xl border border-white/[0.08] bg-dark-800/40 p-8 md:p-10" {...fadeIn(0.2)}>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">What makes SACVPN different</h2>
          <p className="mt-4 text-gray-400 leading-relaxed">
            We focus on clarity and outcomes. Unlimited devices on the Personal / Gaming plan means you do not have to pick which
            device deserves privacy. Our Business tiers translate directly to device quotas so owners can budget confidently.
            We set expectations honestly: distance to server, ISP routing, peering, time of day, and local hardware all influence
            speed and latency, so we do not publish fixed numbers. Judge performance on your own connection.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-6">
              <h4 className="font-semibold text-white">Performance first</h4>
              <p className="text-gray-400 text-sm leading-relaxed mt-2">WireGuard by default. A light footprint and a lean stack for streaming and gaming.</p>
            </div>
            <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-6">
              <h4 className="font-semibold text-white">Simple setup</h4>
              <p className="text-gray-400 text-sm leading-relaxed mt-2">Generate a device, scan a QR code, import the config. That is it.</p>
            </div>
            <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-6">
              <h4 className="font-semibold text-white">Transparent pricing</h4>
              <p className="text-gray-400 text-sm leading-relaxed mt-2">Personal / Gaming $9.99/mo, Business $100 (10 devices), $450 (50), $800 (100). No hidden fees.</p>
            </div>
          </div>
        </motion.div>

        {/* Principles */}
        <motion.div className="mt-12 grid md:grid-cols-2 gap-6" {...fadeIn(0.25)}>
          <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
            <h3 className="text-xl font-semibold text-white mb-4">Principles we operate by</h3>
            <ul className="space-y-2.5">
              <ListItem>Privacy by default. We do not track browsing activity.</ListItem>
              <ListItem>Security by design. Minimal images, patched systems, strong keys.</ListItem>
              <ListItem>Clarity over hype. We prefer truthful ranges to unrealistic promises.</ListItem>
              <ListItem>Speed matters. A private connection should also feel fast.</ListItem>
              <ListItem>Respect for users. No tricks in checkout or cancellation.</ListItem>
              <ListItem>Support that cares. Real people, practical answers.</ListItem>
            </ul>
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
            <h3 className="text-xl font-semibold text-white mb-4">How businesses benefit</h3>
            <ul className="space-y-2.5">
              <ListItem>Device quotas that map to budgets and headcount.</ListItem>
              <ListItem>Centralized device management and revocation.</ListItem>
              <ListItem>Team onboarding with QR instructions for each platform.</ListItem>
              <ListItem>Gaming routes can double as low-overhead paths for real-time apps.</ListItem>
              <ListItem>Option to tailor routing and SLAs at higher tiers.</ListItem>
            </ul>
          </div>
        </motion.div>

        {/* Security and performance */}
        <motion.div className="mt-12" {...fadeIn(0.3)}>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">Security and performance, without the drama</h2>
          <p className="mt-4 text-gray-400 leading-relaxed">
            Security means both protecting data and avoiding complexity that leads to mistakes. We keep our stack lean,
            use modern cryptography, and review changes before rollout. Performance means users do not notice they are on a VPN:
            video calls should be clear, games should feel responsive, and downloads should saturate your local link when possible.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <Stat label="Protocol" value="WireGuard" />
            <Stat label="Encryption" value="ChaCha20" />
            <Stat label="Devices" value="Unlimited" />
            <Stat label="Activity logs" value="None" />
          </div>
          <p className="text-xs text-gray-500 mt-4 leading-relaxed">
            Real speed and latency depend on your distance to the server, ISP routing and peering, device hardware, and network congestion. We do not publish fixed speed or latency numbers because your results are the ones that matter. Judge performance on your own connection.
          </p>
        </motion.div>

        {/* Roadmap */}
        <motion.div className="mt-12 rounded-xl border border-white/[0.08] bg-dark-900 p-8 md:p-10" {...fadeIn(0.35)}>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">Where we are going</h2>
          <p className="mt-4 text-gray-400 leading-relaxed">
            Our roadmap focuses on thoughtful growth: more regions where users need them, smarter routing, and better tooling for teams.
            We are expanding device histories, adding usage insights for those who want them, and continuing to simplify the setup flow
            so first-time users succeed without help.
          </p>
          <ul className="mt-6 space-y-2.5">
            <ListItem>Additional regions with latency-aware routing.</ListItem>
            <ListItem>Team roles and audit logs for Business tiers.</ListItem>
            <ListItem>Self-service key rotation and quick device reissue.</ListItem>
            <ListItem>Optional advanced analytics surfaced in the dashboard.</ListItem>
            <ListItem>More educational content for everyday privacy at home.</ListItem>
          </ul>
        </motion.div>

        {/* Support and contact */}
        <motion.div className="mt-12 grid md:grid-cols-2 gap-6" {...fadeIn(0.4)}>
          <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
            <h3 className="text-xl font-semibold text-white mb-2">Support you can count on</h3>
            <p className="text-gray-400 leading-relaxed">
              We offer US-based email support and read every ticket ourselves, with replies within 24 hours and
              often much sooner during business hours (CT). We focus on practical resolution: getting you online,
              optimizing latency, and solving real problems quickly. Start with our 14-day free trial
              (no credit card required), plus a 30-day money-back guarantee so you can try SACVPN with complete confidence.
            </p>
          </div>
          <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-8">
            <h3 className="text-xl font-semibold text-white mb-2">Our promise</h3>
            <p className="text-gray-400 leading-relaxed">
              We will continue to publish clear pricing, straightforward terms, and realistic performance expectations. We will never
              sell or rent your data. We will treat your time with respect: fast setup, fast responses, and honest communication.
            </p>
          </div>
        </motion.div>

        {/* Company */}
        <motion.div className="mt-12 rounded-xl border border-white/[0.08] bg-dark-900 p-8 md:p-10" {...fadeIn(0.42)}>
          <h2 className="text-2xl md:text-3xl font-display font-bold text-white tracking-tight">Who we are</h2>
          <p className="mt-4 text-gray-400 leading-relaxed">
            SACVPN is a product of StephensCode LLC, a veteran-owned software company based in Conroe, Texas.
            We build and operate SACVPN ourselves, on our own network, so the people writing the code are the
            same people answering your support email.
          </p>
          <p className="mt-4 text-gray-400 leading-relaxed">
            We are also part of a wider circle of veteran-owned and local Texas businesses. See our{" "}
            <Link to="/partners" className="text-brand-400 underline decoration-brand-500/40 underline-offset-2 hover:text-brand-300 transition-colors">
              Trusted Partners
            </Link>{" "}
            page for the companies we work alongside.
          </p>
          <div className="mt-6 flex items-start gap-3 text-gray-400">
            <MapPin className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" />
            <span className="text-sm leading-relaxed">
              StephensCode LLC<br />
              1135 Grand Central Parkway<br />
              Conroe, TX 77304, United States<br />
              support@sacvpn.com
            </span>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-16 relative overflow-hidden rounded-2xl border border-brand-500/20 bg-[radial-gradient(700px_320px_at_50%_-10%,rgba(14,165,233,0.18),transparent_70%)] bg-dark-800 px-8 md:px-14 py-16 text-center"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-md bg-white/5 border border-white/10 mb-6">
            <Zap className="w-3.5 h-3.5 text-brand-400" />
            <span className="text-[13px] tracking-wide text-gray-300">14-day free trial, no credit card required</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-display font-bold text-white tracking-tight">Your privacy. Your control.</h2>
          <p className="mt-4 text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            We built SACVPN for households, gamers, and businesses that want private, simple online protection on every device.
            With a WireGuard-powered network, a no-logs policy on your browsing activity, and transparent pricing, you get strong security without the complexity.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/download"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-brand-500 text-white font-semibold hover:bg-brand-400 transition-colors"
            >
              Start 14-day trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing#business"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors"
            >
              View business plans
            </Link>
          </div>
        </motion.div>
      </section>
    </>
  );
}

/* Small stat tile */
function Stat({ label, value }){
  return (
    <div className="rounded-xl border border-white/[0.08] bg-dark-900 p-5">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-xl font-display font-bold text-white tracking-tight mt-1">{value}</div>
    </div>
  );
}
